import React, { useEffect, useState, ChangeEvent } from "react";
import { RangeSlider } from "rsuite";
import { formatNumberWithCommas } from "@shared/utils";
import { SliderRangeTuple } from "@shared/interfaces";
import "./index.scss";

type Props = {
  rangeFrom: number;
  rangeTo: number;
  defaultRangeTuple: SliderRangeTuple;
  step: number;
  filter: SliderRangeTuple;
  withFormatting?: boolean;
  onChange: (value: SliderRangeTuple) => void;
};

export const Slider = ({ rangeFrom, rangeTo, defaultRangeTuple, step, filter, withFormatting, onChange }: Props) => {
  const [inputRange, setInputRange] = useState<SliderRangeTuple>(filter);
  const [inputText, setInputText] = useState<[string, string]>([filter[0].toString(), filter[1].toString()]);
  const [error, setError] = useState<string | null>(null);

  // Sync when external filter changes
  useEffect(() => {
    setInputRange(filter);
    setInputText([filter[0].toString(), filter[1].toString()]);
  }, [filter]);

  const validateRange = (min: number, max: number) => {
    const isValid = min >= rangeFrom && max <= rangeTo && min <= max;

    setError(isValid ? null : "Value is out of bounds");
    return isValid;
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name as "min" | "max";
    const text = e.target.value;

    const newText: [string, string] = name === "min" ? [text, inputText[1]] : [inputText[0], text];

    setInputText(newText);

    // Only try to parse when both are filled
    if (newText[0] !== "" && newText[1] !== "") {
      const min = parseInt(newText[0]);
      const max = parseInt(newText[1]);

      if (!Number.isNaN(min) && !Number.isNaN(max)) {
        if (validateRange(min, max)) {
          const newRange: SliderRangeTuple = [min, max];
          setInputRange(newRange);
          onChange(newRange);
        }
      }
    }
  };

  const handleSliderChange = (value: SliderRangeTuple) => {
    setInputRange(value);
    setInputText([value[0].toString(), value[1].toString()]);
    onChange(value);
  };

  return (
    <div className="range-slider">
      <input name="min" value={inputText[0]} onChange={handleInputChange} />
      <RangeSlider
        value={inputRange}
        step={step}
        min={rangeFrom}
        max={rangeTo}
        defaultValue={defaultRangeTuple}
        onChange={handleSliderChange}
        renderTooltip={(value) => <span>{withFormatting ? formatNumberWithCommas(value ?? 0) : value}</span>}
      />
      <input name="max" value={inputText[1]} onChange={handleInputChange} />
      {error && <div className="error">{error}</div>}
    </div>
  );
};
